import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

export interface HelpContextSnapshot {
  /** Internal patient identifier of the screen the user is on, when the screen exposes one. */
  patientId: string | null;
  /** Internal visit (transcription) identifier of the screen the user is on, when known. */
  visitId: string | null;
  /** Route with identifiers replaced by `:id` and no query string; safe to send to support and Slack. */
  routeTemplate: string;
}

const PATIENT_ROUTE = /^\/(?:v0\/)?patients\/([^/]+)/;
const VISIT_ROUTE = /^\/(?:v0\/)?transcriptions\/([^/]+)/;

/**
 * Lets Help know which patient/visit the user is looking at without owning any of that state.
 * Screens that keep the selected visit outside the URL (e.g. the Visits list) publish it with
 * {@link setVisit}; everything else is derived from the router URL.
 */
@Injectable({ providedIn: 'root' })
export class HelpContextService {
  private readonly router = inject(Router);

  private readonly publishedVisitId = signal<string | null>(null);

  /** Called by a screen when its selected visit changes; pass null to clear. */
  setVisit(id: string | null | undefined): void {
    this.publishedVisitId.set(id ? String(id) : null);
  }

  snapshot(): HelpContextSnapshot {
    const tree = this.router.parseUrl(this.router.url);
    const path = '/' + tree.root.children['primary']?.segments.map((s) => s.path).join('/');
    const q = tree.queryParams;

    const patientFromRoute = decode(path.match(PATIENT_ROUTE)?.[1]);
    const visitFromRoute = decode(path.match(VISIT_ROUTE)?.[1]);

    return {
      patientId: patientFromRoute ?? stringOrNull(q['patientId']),
      visitId:
        this.publishedVisitId() ??
        visitFromRoute ??
        stringOrNull(q['visit']) ??
        stringOrNull(q['resumeTranscription']),
      routeTemplate: routeTemplate(path),
    };
  }
}

/** Replaces the identifier segment of patient/visit routes so no identifier or name reaches notifications. */
export function routeTemplate(path: string): string {
  const clean = path === '/undefined' ? '/' : path;
  return clean
    .replace(/^(\/(?:v0\/)?patients)\/[^/]+/, '$1/:id')
    .replace(/^(\/(?:v0\/)?transcriptions)\/[^/]+/, '$1/:id');
}

function decode(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
